import Cookies from "js-cookie";

/**
 * Auth used to live in a single `accessToken` cookie shared by the public site
 * and the admin dashboard, so signing into the dashboard silently replaced the
 * logged-in student's session (and vice versa). Tokens are now kept per
 * context: the site keeps `accessToken`, the dashboard keeps `adminAccessToken`.
 *
 * The context is derived from the URL, so it is fixed for the lifetime of a
 * document. Crossing contexts must therefore be a full page load (see
 * `navigateAcrossContext`), never a client-side route change.
 */

const USER_TOKEN_KEY = "accessToken";
const ADMIN_TOKEN_KEY = "adminAccessToken";
const POST_AUTH_REDIRECT_KEY = "postAuthRedirect";

const COOKIE_OPTIONS = { expires: 36500 };

export const isAdminPath = (pathname: string): boolean =>
  pathname.startsWith("/dashboard") ||
  pathname.startsWith("/sales/dashboard") ||
  pathname.startsWith("/login/admin") ||
  pathname.startsWith("/login/sales");

export const isAdminContext = (): boolean =>
  typeof window !== "undefined" && isAdminPath(window.location.pathname);

const currentTokenKey = (): string =>
  isAdminContext() ? ADMIN_TOKEN_KEY : USER_TOKEN_KEY;

const tokenKeyForRole = (role?: string): string =>
  role === "admin" || role === "sales" ? ADMIN_TOKEN_KEY : USER_TOKEN_KEY;

/** Token belonging to the context (site vs dashboard) of the current document. */
export const getAccessToken = (): string | undefined =>
  Cookies.get(currentTokenKey());

export const isLoggedIn = (): boolean => !!getAccessToken();

/** Only allow same-site paths as post-authentication destinations. */
export const isSafeInternalRedirect = (
  path?: string | null
): path is string => Boolean(path?.startsWith("/") && !path.startsWith("//"));

/** Preserve an internal destination while an external OAuth flow is active. */
export const rememberPostAuthRedirect = (path?: string | null): void => {
  if (typeof window === "undefined") return;

  if (isSafeInternalRedirect(path)) {
    window.sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path);
  } else {
    window.sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  }
};

/** Read and clear the OAuth destination so it cannot affect a later login. */
export const consumePostAuthRedirect = (): string | null => {
  if (typeof window === "undefined") return null;

  const path = window.sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
  window.sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  return isSafeInternalRedirect(path) ? path : null;
};

/** Stores the token under the key matching the role that just signed in. */
export const setAccessToken = (token: string, role?: string): void => {
  Cookies.set(tokenKeyForRole(role), token, COOKIE_OPTIONS);
};

/** Clears only the current context's token, leaving the other session intact. */
export const clearAccessToken = (): void => {
  Cookies.remove(currentTokenKey());
};

/** Where a signed-out/expired session should land, per context. */
export const loginPathForContext = (): string => {
  if (typeof window === "undefined") return "/";
  const { pathname } = window.location;
  if (pathname.startsWith("/sales")) return "/login/sales";
  if (isAdminPath(pathname)) return "/login/admin";
  return "/";
};

/**
 * Context the current document booted in. The token key and the persisted store
 * name are bound to it, so a client-side route change into the other context
 * would keep using the wrong session — `hasCrossedContext` detects that.
 */
const bootContextIsAdmin = isAdminContext();

export const hasCrossedContext = (pathname: string): boolean =>
  isAdminPath(pathname) !== bootContextIsAdmin;

/** Persisted store key, kept in step with the token split. */
export const userStoreName = (): string =>
  isAdminContext() ? "adminUserStore" : "userStore";

/**
 * Full page load, required whenever navigation crosses between the site and the
 * dashboard so the new document picks up its own token and store.
 */
export const navigateAcrossContext = (path: string): void => {
  window.location.href = path;
};
